// frontend/src/components/common/ReceiptViewer.jsx
// Modal for viewing receipt images linked to forecast/ledger transactions.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Download, Camera, Loader2 } from 'lucide-react';
import api from '../../utils/api';

/**
 * ReceiptViewer modal — fetches presigned URL and displays receipt image.
 *
 * @param {number} scheduledTransactionId - The forecast item ID to fetch receipts for
 * @param {Function} onClose - Close callback
 */
export default function ReceiptViewer({ scheduledTransactionId, onClose }) {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const fetchReceipts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/api/receipts/by-transaction/${scheduledTransactionId}`);
      setReceipts(res.data.receipts || []);
    } catch (err) {
      setError('Could not load receipt.');
    } finally {
      setLoading(false);
    }
  }, [scheduledTransactionId]);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const activeReceipt = receipts[activeIndex];
  const imageRetryRef = useRef(0);

  const handleImageError = () => {
    // Presigned URL may have expired — re-fetch once only
    if (imageRetryRef.current < 1) {
      imageRetryRef.current += 1;
      fetchReceipts();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative z-10 bg-white w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-lg sm:rounded-xl shadow-xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Camera className="w-4 h-4 text-amber-600" />
            <h3 className="font-semibold text-gray-900">
              Receipt{receipts.length > 1 ? ` (${activeIndex + 1}/${receipts.length})` : ''}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          )}

          {error && !loading && (
            <div className="text-center py-12 text-gray-500">
              <p>{error}</p>
              <button
                onClick={fetchReceipts}
                className="mt-2 text-sm text-blue-600 hover:underline"
              >
                Try again
              </button>
            </div>
          )}

          {!loading && !error && receipts.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No receipt image found.
            </div>
          )}

          {!loading && !error && activeReceipt && (
            <div className="space-y-4">
              {/* Image */}
              <div className="bg-gray-50 rounded-lg overflow-hidden flex items-center justify-center">
                <img
                  src={activeReceipt.url}
                  alt="Receipt"
                  className="max-w-full max-h-[60vh] object-contain"
                  onError={handleImageError}
                />
              </div>

              {/* Extracted data */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm">
                {activeReceipt.extracted_merchant && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Merchant</span>
                    <span className="font-medium text-gray-900">{activeReceipt.extracted_merchant}</span>
                  </div>
                )}
                {activeReceipt.extracted_total != null && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Total</span>
                    <span className="font-medium text-gray-900">
                      ${parseFloat(activeReceipt.extracted_total).toFixed(2)}
                    </span>
                  </div>
                )}
                {activeReceipt.extracted_date && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Date</span>
                    <span className="font-medium text-gray-900">{activeReceipt.extracted_date}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Source</span>
                  <span className="text-gray-600 capitalize">{activeReceipt.upload_source}</span>
                </div>
              </div>

              {/* Multi-receipt navigation */}
              {receipts.length > 1 && (
                <div className="flex justify-center gap-2">
                  {receipts.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveIndex(i)}
                      className={`w-2 h-2 rounded-full ${
                        i === activeIndex ? 'bg-amber-600' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && activeReceipt && (
          <div className="border-t border-gray-200 px-4 py-3 flex justify-end">
            <a
              href={activeReceipt.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700"
            >
              <Download className="w-4 h-4" />
              Open full size
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
